import { inject, Injectable, signal } from '@angular/core';
import { RuntimeConfigService } from './runtime-config.service';

interface OAuthTokens {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

const TOKEN_STORAGE_KEY = 'probeplane.oauth.tokens';
const STATE_STORAGE_KEY = 'probeplane.oauth.state';
const VERIFIER_STORAGE_KEY = 'probeplane.oauth.verifier';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly config = inject(RuntimeConfigService);
  private tokens = this.readTokens();

  readonly authenticated = signal(Boolean(this.tokens?.access_token || this.tokens?.refresh_token));
  readonly initializing = signal(true);
  readonly userEmail = signal(this.readEmail(this.tokens?.id_token));

  isAuthenticated(): boolean {
    return this.authenticated();
  }

  async initialize(): Promise<void> {
    try {
      const params = new URLSearchParams(window.location.search);
      if (window.location.pathname === '/auth/callback' || params.has('code') || params.has('error')) {
        try {
          await this.completeAuthorization(params);
        } finally {
          window.history.replaceState({}, document.title, '/');
        }
      }

      if (this.authenticated()) {
        await this.getAccessToken();
      }
    } catch (error) {
      this.clearSession();
      throw error;
    } finally {
      this.initializing.set(false);
    }
  }

  async signIn(): Promise<void> {
    this.requireConfig();

    const state = this.randomBase64Url(32);
    const verifier = this.randomBase64Url(64);
    const challenge = await this.sha256Base64Url(verifier);
    sessionStorage.setItem(STATE_STORAGE_KEY, state);
    sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);

    const query = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.cognitoClientId,
      redirect_uri: this.redirectUri,
      scope: 'openid email profile',
      state,
      code_challenge_method: 'S256',
      code_challenge: challenge,
    });
    window.location.assign(`${this.config.cognitoDomain}/oauth2/authorize?${query.toString()}`);
  }

  signOut(): void {
    this.clearSession();
    if (!this.config.cognitoDomain || !this.config.cognitoClientId) return;

    const query = new URLSearchParams({
      client_id: this.config.cognitoClientId,
      logout_uri: `${window.location.origin}/`,
    });
    window.location.assign(`${this.config.cognitoDomain}/logout?${query.toString()}`);
  }

  async getAccessToken(): Promise<string> {
    if (this.tokens?.access_token && !this.isExpired(this.tokens.access_token, 60)) {
      return this.tokens.access_token;
    }

    if (!this.tokens?.refresh_token) {
      this.clearSession();
      throw new Error('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
    }

    const refreshed = await this.tokenRequest({
      grant_type: 'refresh_token',
      client_id: this.config.cognitoClientId,
      refresh_token: this.tokens.refresh_token,
    });
    this.storeTokens({
      ...refreshed,
      id_token: refreshed.id_token ?? this.tokens.id_token,
      refresh_token: refreshed.refresh_token ?? this.tokens.refresh_token,
    });
    return refreshed.access_token;
  }

  private async completeAuthorization(params: URLSearchParams): Promise<void> {
    const state = params.get('state');
    const expectedState = sessionStorage.getItem(STATE_STORAGE_KEY);
    if (!state || !expectedState || state !== expectedState) {
      throw new Error('Die Anmeldeantwort konnte nicht sicher überprüft werden. Bitte versuche es erneut.');
    }

    const oauthError = params.get('error');
    const verifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
    try {
      if (oauthError) {
        const description = params.get('error_description');
        throw new Error(description || `Anmeldung fehlgeschlagen (${oauthError}).`);
      }

      const code = params.get('code');
      if (!code || !verifier) {
        throw new Error('Die Anmeldeantwort ist unvollständig. Bitte versuche es erneut.');
      }
      this.storeTokens(await this.tokenRequest({
        grant_type: 'authorization_code',
        client_id: this.config.cognitoClientId,
        code,
        redirect_uri: this.redirectUri,
        code_verifier: verifier,
      }));
    } finally {
      sessionStorage.removeItem(STATE_STORAGE_KEY);
      sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
    }
  }

  private async tokenRequest(parameters: Record<string, string>): Promise<OAuthTokens> {
    this.requireConfig();
    const response = await fetch(`${this.config.cognitoDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(parameters),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error_description?: string; error?: string };
      throw new Error(body.error_description || body.error || 'Cognito konnte die Anmeldung nicht abschließen.');
    }
    const tokens = await response.json() as Partial<OAuthTokens>;
    if (!tokens.access_token || !tokens.expires_in || !tokens.token_type) {
      throw new Error('Cognito hat keine vollständige Token-Antwort geliefert.');
    }
    return tokens as OAuthTokens;
  }

  private storeTokens(tokens: OAuthTokens): void {
    this.tokens = tokens;
    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    this.authenticated.set(true);
    this.userEmail.set(this.readEmail(tokens.id_token));
  }

  private clearSession(): void {
    this.tokens = null;
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
    this.authenticated.set(false);
    this.userEmail.set('');
  }

  private readTokens(): OAuthTokens | null {
    try {
      const value = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      return value ? JSON.parse(value) as OAuthTokens : null;
    } catch {
      return null;
    }
  }

  private readEmail(idToken?: string): string {
    if (!idToken) return '';
    try {
      const payload = idToken.split('.')[1];
      if (!payload) return '';
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(normalized)) as { email?: string };
      return claims.email ?? '';
    } catch {
      return '';
    }
  }

  private isExpired(token: string, clockSkewSeconds: number): boolean {
    try {
      const payload = token.split('.')[1];
      if (!payload) return true;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(normalized)) as { exp?: number };
      return !claims.exp || claims.exp <= Math.floor(Date.now() / 1000) + clockSkewSeconds;
    } catch {
      return true;
    }
  }

  private randomBase64Url(byteLength: number): string {
    const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
    return this.base64Url(bytes);
  }

  private async sha256Base64Url(value: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return this.base64Url(new Uint8Array(digest));
  }

  private base64Url(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => binary += String.fromCharCode(byte));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private requireConfig(): void {
    if (!this.config.cognitoDomain || !this.config.cognitoClientId) {
      throw new Error('Die Cognito-Managed-Login-Konfiguration fehlt.');
    }
  }

  private get redirectUri(): string {
    return `${window.location.origin}/auth/callback`;
  }
}
